import React, { useMemo } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import { Box } from "@mui/system";

const COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
];

const DEBUG = false; // Nustatykite į true, kad įjungtumėte derinimo žurnalus

const debugLog = (...args) => {
  if (DEBUG) {
  }
};

const BarChart = ({ klausimas, atsakymai }) => {
  const data = useMemo(() => {
    debugLog('Checkbox BarChart received:', { klausimas, atsakymai });
    if (!atsakymai?.length) {
      debugLog('Checkbox BarChart returning empty - no answers');
      return [];
    }

    debugLog('Processing answers for checkbox chart');

    // Initialize counts map
    const selectionCounts = new Map();
    
    debugLog('Question name:', klausimas.name);
    
      // For each answer, check the object with checkbox selections
      atsakymai.forEach((atsakymas, idx) => {
        debugLog(`Processing answer ${idx}:`, JSON.stringify(atsakymas, null, 2));
        
        // Handle both direct array and object formats
          let allAnswers = [];
        
          // Try different paths to find the answer
          const possiblePaths = [
            atsakymas?.attributes?.atsakymai?.[klausimas.name],
            atsakymas?.attributes?.atsakymai?.attributes?.[klausimas.name],
            atsakymas?.atsakymai?.[klausimas.name],
            atsakymas?.attributes?.[klausimas.name],
            atsakymas?.[klausimas.name],
            // Look for any key that ends with the question name
            ...Object.entries(atsakymas?.attributes?.atsakymai || {})
              .filter(([key]) => key.endsWith(klausimas.name))
              .map(([_, value]) => value)
          ].filter(Boolean); // Remove null/undefined values
          debugLog('Checking possible paths:', possiblePaths);
          
          // Get first non-null value
          const directAnswer = possiblePaths.find(path => path != null);
          debugLog('Found direct answer:', directAnswer);
          
          // Handle different formats of answer
          if (Array.isArray(directAnswer)) {
            allAnswers = directAnswer;
          } else if (typeof directAnswer === 'object' && directAnswer !== null) {
            // Check both key-value and value-only formats
            if (Object.values(directAnswer).some(v => v === "Taip" || v === "10")) {
              // If it's an object with "Taip" or "10" values
              allAnswers = Object.entries(directAnswer)
                .filter(([_, value]) => value === "Taip" || value === "10")
                .map(([key]) => key);
            } else {
              // If the keys themselves are the selections
              allAnswers = Object.keys(directAnswer);
            }
          } else {
            // Special handling for "2.4.2" type questions
            if (klausimas.name.startsWith('2.4.2')) {
              // Look for exact match first
              const exactMatch = atsakymas?.attributes?.atsakymai?.[klausimas.name];
              if (exactMatch) {
                allAnswers = [exactMatch];
              } else {
                // Otherwise look through all fields that could be checkbox answers
                const allFields = atsakymas?.attributes?.atsakymai || {};
                allAnswers = Object.entries(allFields)
                  .filter(([key, value]) => (
                    key.includes('2.4.2') && 
                    (Array.isArray(value) || typeof value === 'object')
                  ))
                  .map(([_, value]) => value);
              }
            } else {
              // Standard field search for other questions
              allAnswers = Object.entries(atsakymas?.attributes?.atsakymai || {})
                .filter(([key]) => key === klausimas.name)
                .map(([_, value]) => value);
            }
          }
          
          debugLog('Extracted answers:', allAnswers);
        
        debugLog('All answers found for this question:', JSON.stringify(allAnswers));
        
        allAnswers.forEach(selections => {
          try {
            // If selections is a string (JSON array), try to parse it
            if (typeof selections === 'string' && selections.startsWith('[')) {
              selections = JSON.parse(selections);
            }
            
            // Normalize the selections to array format
            let selectionsArray;
            if (typeof selections === 'string') {
              selectionsArray = [selections];
            } else if (Array.isArray(selections)) {
              // Check if array contains objects
              if (selections.length > 0 && typeof selections[0] === 'object') {
                // Try to extract values from common properties
                selectionsArray = selections.map(item => 
                  item.value || item.label || item.text || item.name || item.toString()
                );
              } else {
                // Flatten in case we have array of arrays
                selectionsArray = selections.flat();
              }
            } else if (selections && typeof selections === 'object') {
              // If it's an object with a value property
              if ('value' in selections || 'label' in selections) {
                selectionsArray = [selections.value || selections.label];
              } else {
                selectionsArray = Object.values(selections);
              }
            } else {
              selectionsArray = [];
            }
            
            debugLog('Normalized selections array:', selectionsArray);
            
            // Count each valid selection
            // Filter out empty values first
            const validSelections = selectionsArray.filter(selection => 
              selection !== undefined && 
              selection !== null && 
              selection !== '' &&
              String(selection).trim() !== ''
            );
            
            debugLog('Valid selections:', validSelections);
            
            validSelections.forEach(selection => {
              const selectionStr = String(selection).trim();
              debugLog('Processing valid selection:', selectionStr);
              selectionCounts.set(selectionStr, (selectionCounts.get(selectionStr) || 0) + 1);
            });
          } catch (error) {
            if (DEBUG) console.error('Error processing selections:', error);
          }
        });
      });

    debugLog('Final counts:', Object.fromEntries(selectionCounts));

    // Convert to chart data format
    return Array.from(selectionCounts.entries())
      .map(([name, count]) => ({
        name,
        value: count,
        percentage: (count / atsakymai.length) * 100
      }))
      .sort((a, b) => b.percentage - a.percentage); // Sort by percentage descending
  }, [klausimas, atsakymai]);

  if (!data.length) return null;

  return (
    <Box sx={{ width: "100%", height: 500, mt: 2 }}>
      <ResponsiveContainer>
        <RechartsBarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 150 // Increased bottom margin for long labels
          }}
          height={500} // Increased height
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            interval={0}
            height={120}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            tickFormatter={(value) => `${value}%`}
            domain={[0, 100]}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value) => [`${value.toFixed(1)}%`, "Pasirinkta"]}
            labelStyle={{ fontSize: 12 }}
          />
          <Bar dataKey="percentage" fill="#8884d8">
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default BarChart;
